import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { getAppConfig } from '@/config/AppConfig';
import { useInlineMemo } from '@/util/useMemoStyles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMemo, useCallback, useState } from 'react';
import {
  useRecordContext,
  useRefresh,
  Labeled,
  UpdateButton,
} from 'react-admin';
import { Delete, Visibility } from '@mui/icons-material';
import ReactJson from 'react-json-view';

const appConfig = getAppConfig();

export const NukeLegacyFiles = () => {
  const refresh = useRefresh();

  const therapist = useRecordContext();

  const nukeLegacyFiles = useMemo(
    () => therapist?.nukeLegacyFiles || {},
    [therapist],
  );

  const [outputDialogOpen, setOutputDialogOpen] = useState(false);

  const onViewOutput = useCallback(() => {
    setOutputDialogOpen(true);
  }, []);

  const onCloseOutput = useCallback(() => {
    setOutputDialogOpen(false);
  }, []);

  if (!therapist) {
    return null;
  }

  return (
    <>
      <Box paddingTop="1.5rem" display="flex">
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          paddingTop=".5rem"
        >
          <Labeled label="Last Requested At">
            {nukeLegacyFiles.lastRequestedAt ? (
              <GenericRelativeDateDisplay
                date={nukeLegacyFiles.lastRequestedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          <Labeled marginLeft="1rem" label="Last Completed At">
            {nukeLegacyFiles.lastCompletedAt ? (
              <GenericRelativeDateDisplay
                date={nukeLegacyFiles.lastCompletedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          {nukeLegacyFiles.output && (
            <Box marginLeft="1rem">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={onViewOutput}
              >
                <Visibility />
              </Button>
            </Box>
          )}
          <Box marginLeft="1rem">
            <NukeLegacyFilesButton
              therapistId={therapist.id as any}
            />
          </Box>
        </Box>
      </Box>
      <Dialog open={outputDialogOpen} onClose={onCloseOutput}>
        <DialogTitle>Nuke Legacy Files Output</DialogTitle>
        <DialogContent>
          <ReactJson
            src={nukeLegacyFiles.output}
            theme="monokai"
            displayDataTypes={false}
            enableClipboard={true}
            collapsed={2}
            style={{
              backgroundColor: 'transparent',
              fontFamily: 'monospace',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseOutput}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export type SystemSnapshotButtonProps = {
  therapistId: string;
  label?: string;
  confirmTitle?: string;
  confirmContent?: string;
};

export type ForeignEntitySystemSnapshotProps = {
  therapistId: string;
};

export const NukeLegacyFilesButton = ({
  therapistId,
  label = 'Delete Legacy Files',
  confirmTitle = 'Are you sure?',
  confirmContent = "This will nuke all legacy .wav files from the Therapist's device. This is perfectly safe, but please use it sparingly.",
}: SystemSnapshotButtonProps) => {
  const refresh = useRefresh();

  return (
    <UpdateButton
      label={label}
      resource="therapists"
      id={therapistId}
      color="warning"
      variant="outlined"
      mutationMode="pessimistic"
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      icon={<Delete />}
      sx={{ marginLeft: '1rem' }}
      mutationOptions={{ onSuccess: refresh }}
      data={useInlineMemo({
        nukeLegacyFiles: JSON.stringify({
          lastRequestedAt: new Date().toISOString(),
          lastCompletedAt: null,
        }),
      })}
    />
  );
};
